import jwtDecode from "jwt-decode";

import {
  logout,
  refreshAccessToken,
  refreshAccessTokenPair,
  tokenRetrieved,
} from "../actions/authActions";
import { SYSTEM_HEARTBEAT } from "../actions/constants";
import { getLastUserInteractionTimestamp } from "../hooks/useInteractionListener";
import { AuthState, Jwt } from "../reducers/authReducer";
import { getAuthCookie } from "../utils/cookie";

interface HBPayload {
  index: number;
}

const ONE_MINUTE_MS = 60 * 1000;

// HEARTBEAT_FREQUENCY is how frequently we check for a pulse
const HEARTBEAT_FREQUENCY_MS = ONE_MINUTE_MS;

// ACCESS_LEEWAY is how soon before expiration we request a new access token
// Should be at least 2x HEARTBEAT_FREQUENCY in order to guarantee a refresh before expiration
const ACCESS_LEEWAY_SECONDS = (5 * HEARTBEAT_FREQUENCY_MS) / 1000;

// REFRESH_LEEWAY is how soon before expiration we request a new refresh token
// Should be higher than ACCESS_LEEWAY in order to minimize the number of requests
const REFRESH_LEEWAY_SECONDS = (6 * HEARTBEAT_FREQUENCY_MS) / 1000;

// Maximum period of inactivity before the token is not refreshed on your behalf (akin to a screen saver timeout).
// Reasonable values could be as low as 5 minutes; users will prefer larger values.
const MAX_INACTIVITY_MS = 29 * ONE_MINUTE_MS;

let heartbeatTimeout: ReturnType<typeof setTimeout> | null = null;

const heartbeatMiddleware = (store: any) => (next: any) => (action: any) => {
  next(action);

  // Only perform heartbeat after SYSTEM_HEARTBEAT action
  if (action.type !== SYSTEM_HEARTBEAT) return;

  if (heartbeatTimeout) clearTimeout(heartbeatTimeout);
  const { index } = action.payload as HBPayload;
  const secondsNow = Date.now() / 1000;

  // Skip heartbeat when window is hidden and resume when it becomes visible again
  if (document.visibilityState !== "visible") {
    document.addEventListener(
      "visibilitychange",
      () => {
        store.dispatch({
          type: SYSTEM_HEARTBEAT,
          payload: {
            index: index + 1,
          },
        });
      },
      { once: true }
    );

    return;
  }

  // Check for fresh cookies before proceeding with heartbeat
  const authCookie = getAuthCookie();

  if (authCookie) {
    // A cookie was found so we try to decode it.
    // If there's an error then we just continue as if nothing happened.
    try {
      const cookieAccessJwt: Jwt = jwtDecode(authCookie.accessToken);
      const storeAccessJwt = store.getState().auth.access;
      if (
        cookieAccessJwt.user_id === storeAccessJwt.user_id &&
        cookieAccessJwt.exp > storeAccessJwt.exp
      ) {
        // The cookie token is valid, matches our current user, and is more recent than ours
        // so now we dispatch a tokenRetrieved() action with the cookie tokens.
        store.dispatch(
          tokenRetrieved({
            access: authCookie.accessToken,
            refresh: authCookie.refreshToken,
          })
        );
      }
    } catch (e) {}
  }

  // Get auth state AFTER possibly reloading fresh cookies
  const auth: AuthState = store.getState().auth;
  const { access, refresh, longLivedToken } = auth;
  if (longLivedToken) {
    // If the token does not expire, we don't need subsequent heartbeats
    return;
  }

  if (!access || !refresh) {
    console.error(
      "Heartbeat should not be running without access and refresh tokens"
    );
    store.dispatch(logout());
    return;
  }

  // Determine access token validity
  const secondsRemainingAccess = access.exp - secondsNow;
  const expiringAccess = secondsRemainingAccess <= ACCESS_LEEWAY_SECONDS;

  // Determine refresh token validity
  const secondsRemainingRefresh = refresh.exp - secondsNow;
  const expiringRefresh = secondsRemainingRefresh <= REFRESH_LEEWAY_SECONDS;
  const expiredRefresh = secondsRemainingRefresh <= 0;

  // Determine user activity
  const lastInteractionMs = getLastUserInteractionTimestamp();
  const userInactive = Date.now() - lastInteractionMs >= MAX_INACTIVITY_MS;

  // Force logout and end heartbeat if refresh token is expired or user is inactive
  if (expiredRefresh || userInactive) {
    store.dispatch(logout());
    return;
  }

  // Dispatch token requests if necessary
  if (expiringRefresh) {
    store.dispatch(refreshAccessTokenPair());
  } else if (expiringAccess) {
    store.dispatch(refreshAccessToken());
  }

  // Set timeout for next heartbeat
  heartbeatTimeout = setTimeout(() => {
    store.dispatch({
      type: SYSTEM_HEARTBEAT,
      payload: {
        index: index + 1,
      },
    });
  }, HEARTBEAT_FREQUENCY_MS);
};

export default heartbeatMiddleware;
